import type { HumanizeDurationOptions } from 'humanize-duration-ts';
import {
  HumanizeDuration,
  HumanizeDurationLanguage,
} from 'humanize-duration-ts';

import { assertValue, roundUp, getDomain } from '@libs/utils';
import type { PrecisionTypes } from '@libs/sembo-dates';
import { SemboDate, DateFormat, durationInMs } from '@libs/sembo-dates';
import type { IngredientId } from '@libs/trip-planner-interfaces';

const durationHumanizer = getHumanizer();

const TIME_CONVERSION = 60;
const DECIMAL_CONVERSION = 1000;

export function milisecondsToHHMM(miliseconds: number): string {
  const timeInMinutes = Math.floor(
    miliseconds / (TIME_CONVERSION * DECIMAL_CONVERSION)
  );
  const hours = Math.floor(timeInMinutes / TIME_CONVERSION);
  const minutes = timeInMinutes % TIME_CONVERSION;
  const formatedHHMM = `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`;
  return formatedHHMM;
}

export function HHMMToMiliseconds(HHMM: string): number {
  const [hours, minutes] = HHMM.split(':').map((value) => parseInt(value, 10));
  assertValue(hours, 'formatting.ts > HHMMToMiliseconds > hours');
  assertValue(minutes, 'formatting.ts > HHMMToMiliseconds > minutes');

  const timeInMinutes = hours * TIME_CONVERSION + minutes;
  const timeInMiliseconds =
    timeInMinutes * TIME_CONVERSION * DECIMAL_CONVERSION;
  return timeInMiliseconds;
}

export function dashToUnderscore(text: string): string {
  return text.replaceAll('-', '_');
}

export function textFormaterByLocale(text: string): string {
  if (getDomain.isGermanMarket) {
    return text;
  }
  return text.toLowerCase();
}

export function getLongDatePeriod(
  fromDate: Date,
  toDate: Date,
  returnToDate?: boolean
): string {
  const semboDateFrom = new SemboDate(fromDate);
  const semboDateTo = new SemboDate(toDate);

  if (returnToDate) {
    return semboDateTo.format(DateFormat.DayShortMonthShortYear);
  }

  const isSameYear = semboDateFrom.isSameYear(toDate);
  const isSameMonth = semboDateFrom.isSameMonth(toDate);
  const isSameDay = semboDateFrom.isSameDay(toDate);

  if (isSameDay) {
    // "Mon, 15 Sep 2024"
    return formatWithCondition(
      semboDateFrom,
      DateFormat.DayShortMonthShortYear
    );
  }

  if (isSameMonth && isSameYear) {
    // "Mon, 15 - Wed, 17 Sep 2024"
    return `${formatWithCondition(
      semboDateFrom,
      DateFormat.DayAndShortDate
    )} - ${formatWithCondition(
      semboDateTo,
      DateFormat.DayShortMonthShortYear
    )}`;
  }

  if (isSameYear) {
    // "Mon, 30 May - Wed, 5 Jun 2024"
    return `${formatWithCondition(
      semboDateFrom,
      DateFormat.DayShortMonthShort
    )} - ${formatWithCondition(
      semboDateTo,
      DateFormat.DayShortMonthShortYear
    )}`;
  }

  // "Mon, 30 Dec 2023 - Wed, 5 Jan 2024"
  return `${formatWithCondition(
    semboDateFrom,
    DateFormat.DayShortMonthShortYear
  )} - ${formatWithCondition(semboDateTo, DateFormat.DayShortMonthShortYear)}`;
}

export function getShortDatePeriod(
  fromDate: Date,
  toDate: Date,
  returnToDate?: boolean,
  isSmallDevice?: boolean
): string {
  const semboDateFrom = new SemboDate(fromDate);
  const semboDateTo = new SemboDate(toDate);
  if (returnToDate) {
    return semboDateTo.format(DateFormat.DayShortMonthShortYear);
  }

  const isSameYear = semboDateFrom.isSameYear(toDate);
  const isSameMonth = semboDateFrom.isSameMonth(toDate);
  const isSameDay = semboDateFrom.isSameDay(toDate);

  if (isSameDay) {
    // "15 September 2024" or "15 Sep 2024"
    return formatWithCondition(
      semboDateFrom,
      isSmallDevice
        ? DateFormat.DateWithShortMonth
        : DateFormat.DateWithFullMonth
    );
  }

  if (isSameMonth && isSameYear) {
    // "15 - 17 September 2024" or "15 - 17 Sep 2024"
    return `${formatWithCondition(
      semboDateFrom,
      DateFormat.Day
    )} - ${formatWithCondition(
      semboDateTo,
      isSmallDevice
        ? DateFormat.DateWithShortMonth
        : DateFormat.DateWithFullMonth
    )}`;
  }

  if (isSameYear) {
    // "30 May - 5 June 2024" or "30 May - 5 Jun 2024"
    return `${formatWithCondition(
      semboDateFrom,
      isSmallDevice ? DateFormat.DateMonth : DateFormat.DateFullMonthName
    )} - ${formatWithCondition(
      semboDateTo,
      isSmallDevice
        ? DateFormat.DateWithShortMonth
        : DateFormat.DateWithFullMonth
    )}`;
  }

  // "30 December 2023 - 5 January 2024" or "30 Dec 2023 - 5 Jan 2024"
  return `${formatWithCondition(
    semboDateFrom,
    isSmallDevice ? DateFormat.DateWithShortMonth : DateFormat.DateWithFullMonth
  )} - ${formatWithCondition(
    semboDateTo,
    isSmallDevice ? DateFormat.DateWithShortMonth : DateFormat.DateWithFullMonth
  )}`;
}

function formatWithCondition(semboDate: SemboDate, format: DateFormat): string {
  const formattedDate = semboDate.format(format);

  return formattedDate.includes('.')
    ? formattedDate.replace(', ', ' ')
    : formattedDate;
}

export function formatDistance(distanceInMeters: number): string {
  const metersInKM = 1000;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  const distanceInKM = Math.round((distanceInMeters / metersInKM) * 10) / 10;
  return distanceInMeters < metersInKM
    ? `${distanceInMeters} m`
    : `${distanceInKM} km`;
}

export function getHumanizedDuration(
  duration: number,
  unit: PrecisionTypes
): string {
  return durationHumanizer.humanize(durationInMs(duration, unit));
}

export declare type DurationUnitName =
  | 'hours'
  | 'minutes'
  | 'seconds'
  | 'milliseconds';

function getHumanizer(): HumanizeDuration {
  const languageService = new HumanizeDurationLanguage();
  const humanizer = new HumanizeDuration(languageService);

  const humanizerOptions: HumanizeDurationOptions = {
    language: 'en',
    languages: {
      en: {
        // eslint-disable-next-line id-denylist
        y: () => 'y',
        mo: () => 'mo',
        w: () => 'w',
        d: () => 'd',
        h: () => 'h',
        m: () => 'min',
        s: () => 's',
        ms: () => 'ms',
        decimal: '.',
      },
    },
    units: ['h', 'm'],
    round: true,
    serialComma: false,
    spacer: '',
    delimiter: ' ',
  };

  humanizer.setOptions(humanizerOptions);

  return humanizer;
}

export function getHotelKey(
  hotelCode: string,
  ingredientId: IngredientId
): string {
  return hotelCode + ingredientId;
}

export function getFormattedTime(date: Date | string): string {
  return new SemboDate(date).format(DateFormat.Time);
}

export function getFormattedDateTime(date: Date | string): string {
  return new SemboDate(date).format(DateFormat.DateTime);
}

export function getFormattedDateMonth(date: Date): string {
  return new SemboDate(date).format(getDateMonthFormatBasedOnMarket());
}

function getDateMonthFormatBasedOnMarket(): DateFormat {
  return getDomain.isDanishMarket
    ? DateFormat.DateMonthWithDot
    : DateFormat.DateMonth;
}

export function getDifferenceInDays(
  date1: Date | string,
  date2: Date | string
): number {
  const semboDate1 = new SemboDate(date1);
  const semboDate2 = new SemboDate(date2);
  return semboDate1.diff(semboDate2.get(), 'calendarDay');
}

function getHoursFromMinutes(min: number): number {
  return min / TIME_CONVERSION;
}

export function getRoundedUpHoursFromMinutes(min: number): number {
  return roundUp(getHoursFromMinutes(min), 2);
}

function getMinutesFromHours(hours: number): number {
  return hours * TIME_CONVERSION;
}

export function getRoundedMinutesFromHours(hours: number): number {
  return Math.round(getMinutesFromHours(hours));
}

export function getHHMMFormatFromMinutes(timeInMin: number): string {
  const hours = Math.floor(timeInMin / TIME_CONVERSION);
  const minutes = timeInMin % TIME_CONVERSION;

  const formattedHours =
    hours > 0 ? `${hours.toString().padStart(2, '0')}h ` : '';
  const formattedMinutes =
    minutes > 0 ? `${minutes.toString().padStart(2, '0')}min` : '00min';
  return `${formattedHours}${formattedMinutes}`;
}
